import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Brain, Loader, Minimize2, Maximize2 } from 'lucide-react';
import { aiAPI } from '../../services/api';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  context?: string;
}

interface AIAssistantBadgeProps {
  pageContext?: string;
  className?: string;
}

const AIAssistantBadge: React.FC<AIAssistantBadgeProps> = ({ 
  pageContext = 'general',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize with context-aware welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = getContextualWelcome(pageContext);
      setMessages([{
        id: '1',
        type: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
        context: pageContext
      }]);
    }
  }, [isOpen, pageContext]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getContextualWelcome = (context: string): string => {
    const welcomeMessages = {
      'smart-kitchen': "👋 Hi! I'm your Smart Kitchen AI Assistant. I can help you with IoT device management, cooking optimization based on sensor data, recipe adjustments for current conditions, and troubleshooting kitchen equipment. What would you like to know?",
      'food-scanner': "🔍 Welcome to the Food Scanner! I can help you understand nutrition labels, identify foods in images, suggest healthier alternatives, and provide detailed nutritional analysis. How can I assist with your food recognition needs?",
      'recipe-generator': "👨‍🍳 Hello! I'm here to help you create amazing recipes. I can suggest ingredient combinations, modify recipes for dietary restrictions, provide cooking techniques, and help you make the most of what you have in your kitchen. What would you like to cook?",
      'nutrition-analysis': "📊 Hi there! I can help you interpret your nutrition data, suggest improvements to your diet, explain nutritional concepts, and provide personalized recommendations based on your health goals. What nutrition questions do you have?",
      'meal-planner': "📅 Welcome to meal planning! I can help you create balanced weekly menus, accommodate dietary preferences, optimize for nutrition goals, and suggest efficient meal prep strategies. Ready to plan some delicious meals?",
      'dashboard': "🏠 Welcome to your FoodMind dashboard! I can help you understand your nutrition metrics, set health goals, interpret your progress charts, and provide personalized recommendations. How can I help you today?",
      'recipes': "🍽️ Hi! I'm here to help you discover and perfect recipes. I can suggest modifications, explain cooking techniques, help with ingredient substitutions, and provide nutritional insights for any recipe. What are you cooking today?",
      'ar-cooking': "🥽 Welcome to AR Cooking! I can guide you through immersive cooking experiences, provide real-time cooking tips, help with technique visualization, and troubleshoot any AR cooking challenges. Ready to cook in augmented reality?",
      'health-integration': "❤️ Hello! I can help you connect your health devices, interpret fitness data in relation to nutrition, suggest diet adjustments based on your activity, and optimize your nutrition for your health goals. How can I assist?",
      'subscription': "💎 Hi! I can help you understand our subscription features, compare plan benefits, suggest the best plan for your needs, and answer any questions about FoodMind's premium capabilities. What would you like to know?",
      'general': "🧠 Hello! I'm your FoodMind AI Assistant, powered by advanced AI. I can help with nutrition advice, cooking tips, recipe suggestions, meal planning, food analysis, and health guidance. I'm here to make your healthy eating journey easier and more enjoyable. What can I help you with today?"
    };

    return welcomeMessages[context as keyof typeof welcomeMessages] || welcomeMessages.general;
  };

  const getContextualPrompt = (context: string, userMessage: string): string => {
    const contextPrompts = {
      'smart-kitchen': `You are a Smart Kitchen AI Assistant for FoodMind. The user is on the Smart Kitchen page with IoT devices. Help with: device management, sensor data interpretation, cooking optimization, temperature/humidity guidance, recipe adjustments based on real-time conditions, and kitchen equipment troubleshooting. User question: "${userMessage}"`,
      'food-scanner': `You are a Food Recognition AI Assistant for FoodMind. The user is using the food scanner feature. Help with: food identification, nutritional analysis, ingredient recognition, health assessments, dietary recommendations, and food safety information. User question: "${userMessage}"`,
      'recipe-generator': `You are a Recipe Generation AI Assistant for FoodMind. Help users create and modify recipes, suggest ingredient combinations, provide cooking techniques, accommodate dietary restrictions, and optimize recipes for nutrition and taste. User question: "${userMessage}"`,
      'nutrition-analysis': `You are a Nutrition Analysis AI Assistant for FoodMind. Help users understand their nutritional data, provide dietary recommendations, explain nutrition science, suggest improvements, and interpret health metrics. User question: "${userMessage}"`,
      'meal-planner': `You are a Meal Planning AI Assistant for FoodMind. Help users create balanced meal plans, accommodate dietary preferences, optimize for nutrition goals, suggest meal prep strategies, and plan efficient shopping lists. User question: "${userMessage}"`,
      'dashboard': `You are a Dashboard AI Assistant for FoodMind. Help users understand their nutrition dashboard, interpret progress charts, set health goals, track metrics, and provide personalized recommendations based on their data. User question: "${userMessage}"`,
      'recipes': `You are a Recipe AI Assistant for FoodMind. Help users find recipes, make modifications, explain cooking techniques, suggest ingredient substitutions, provide nutritional insights, and improve cooking skills. User question: "${userMessage}"`,
      'ar-cooking': `You are an AR Cooking AI Assistant for FoodMind. Help users with augmented reality cooking experiences, provide real-time guidance, explain 3D cooking visualizations, troubleshoot AR features, and enhance immersive cooking. User question: "${userMessage}"`,
      'health-integration': `You are a Health Integration AI Assistant for FoodMind. Help users connect health devices, interpret fitness data, correlate activity with nutrition, suggest diet adjustments, and optimize nutrition for health goals. User question: "${userMessage}"`,
      'subscription': `You are a Subscription AI Assistant for FoodMind. Help users understand subscription features, compare plans, explain benefits, assist with account management, and recommend the best plan for their needs. User question: "${userMessage}"`,
      'general': `You are FoodMind's AI Assistant, an expert in nutrition, cooking, meal planning, and healthy eating. Provide helpful, accurate, and personalized advice. User question: "${userMessage}"`
    };

    return contextPrompts[context as keyof typeof contextPrompts] || contextPrompts.general;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date(),
      context: pageContext
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const contextualPrompt = getContextualPrompt(pageContext, inputValue);
      const conversationHistory = messages.slice(-5); // Last 5 messages for context
      
      const response = await aiAPI.chat(contextualPrompt, conversationHistory);
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        context: pageContext
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment. In the meantime, you can explore FoodMind's features or check our help documentation.",
        timestamp: new Date(),
        context: pageContext
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    const welcomeMessage = getContextualWelcome(pageContext);
    setMessages([{
      id: '1',
      type: 'assistant',
      content: welcomeMessage,
      timestamp: new Date(),
      context: pageContext
    }]);
  };

  return (
    <>
      {/* AI Assistant Badge */}
      <motion.div
        className={`fixed bottom-6 right-6 z-50 ${className}`}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.5, type: "spring" }}
      >
        {!isOpen && (
          <motion.button
            onClick={() => setIsOpen(true)}
            className="group relative w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Brain className="w-8 h-8 text-white" />
            
            {/* Pulse animation */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-ping opacity-20"></div>
            
            {/* AI indicator */}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-black/80 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              AI Assistant
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/80"></div>
            </div>
          </motion.button>
        )}
      </motion.div>

      {/* AI Assistant Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ 
              opacity: 1, 
              scale: isMinimized ? 0.3 : 1, 
              y: 0,
              x: isMinimized ? 200 : 0
            }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, type: "spring" }}
            className={`fixed bottom-6 right-6 z-50 ${
              isMinimized ? 'w-20 h-16' : 'w-96 h-[500px]'
            } bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                {!isMinimized && (
                  <div>
                    <h3 className="text-white font-semibold">AI Assistant</h3>
                    <p className="text-purple-300 text-xs">Powered by OpenAI</p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                {!isMinimized && (
                  <button
                    onClick={clearConversation}
                    className="p-1 text-purple-400 hover:text-purple-300 transition-colors"
                    title="Clear conversation"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                )}
                
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 text-purple-400 hover:text-purple-300 transition-colors"
                >
                  {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </button>
                
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 h-80">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-sm px-4 py-2 rounded-2xl ${
                          message.type === 'user'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                            : 'bg-white/10 text-white border border-white/20'
                        }`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                        {message.type === 'assistant' && (
                          <div className="flex items-center space-x-1 mt-2 opacity-70">
                            <Sparkles className="w-3 h-3" />
                            <span className="text-xs">AI-powered</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-white/10 border border-white/20 px-4 py-2 rounded-2xl">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything about nutrition, cooking, or health..."
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      disabled={isLoading}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-purple-300 mt-2 flex items-center">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Powered by OpenAI • Context-aware assistance
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistantBadge;